import clsx from 'clsx';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertTooltip from 'components/HubertUI/HubertTooltip';
import HubertPopover from 'components/HubertUI/HubertPopover';
import MutedText from 'components/common/MutedText';
import OptionAction from './OptionAction';
import { Zap } from 'lucide-react';
import { isRecruiterUser } from 'types/user.types';
import { useInTemplateEditAndUneditable } from 'hooks/useInTemplateEditAndUneditable';
import { useUser } from 'hooks/useUser';

import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { selectQuestionMode, selectQuestionLanguage } from 'rtk/questionEditorSelector';
import { addActionToOption } from 'rtk/Question.Editor.Slice';
import { useQuestionEditorContext } from '../context/create-question.context';

interface Props extends ComponentProps<typeof HubertPopover> {
  actions: string[];
  fieldIndex: number;
}

const OptionActionPopover = ({ actions, fieldIndex, ...props }: Props) => {
  const { t } = useTranslation('Questionsv2');
  const customContainer = document.getElementById('main-content') as HTMLElement;
  const user = useUser();
  const isRecruiter = isRecruiterUser(user);
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const inTemplateEditAndUneditable = useInTemplateEditAndUneditable(questionId, mode);

  if (isRecruiter) {
    return null;
  }

  return (
    <HubertPopover {...props}>
      <HubertTooltip>
        <HubertTooltip.Trigger asChild>
          <HubertPopover.Trigger asChild>
            <HubertButton
              variant="secondary"
              className="cursor-pointer"
              icon={<Zap className={actions.length ? 'text-primary-500' : 'text-gray-500'} />}
            ></HubertButton>
          </HubertPopover.Trigger>
        </HubertTooltip.Trigger>
        <HubertTooltip.Content>{t('optionActions.tooltip')}</HubertTooltip.Content>
      </HubertTooltip>

      <HubertPopover.Content
        className={clsx('flex flex-col justify-between', 'min-h-[100px]', 'max-w-[500px] min-w-[500px]', 'p-[12px]')}
        portalContent={true}
        portalElement={customContainer}
      >
        <div className={clsx('mr-6 flex flex-row justify-between')}>
          <div>
            {/* TODO(i18n): Category 4 — "Actions" label is followed by a MutedText subtitle that together form one logical unit; restructure as full sentence before extracting */}
            Actions <MutedText> - If this option is selected, this will happen.</MutedText>
          </div>

          <HubertButton
            size="xs"
            disabled={inTemplateEditAndUneditable}
            onClick={() => {
              dispatch(addActionToOption({ questionId, index: fieldIndex, language: questionLanguage }));
            }}
          >
            {t('optionActions.addAction')}
          </HubertButton>
        </div>

        <div
          className={clsx('flex flex-col justify-start gap-2', 'max-h-[200px] min-h-[75px]', 'my-4', 'overflow-auto')}
        >
          {actions.length === 0 ? (
            <div className={clsx('justify-center', 'grow', 'hubert-vertical-center')}>
              <MutedText>{t('optionActions.noActions')}</MutedText>
            </div>
          ) : (
            actions.map((action, index) => {
              return (
                <OptionAction
                  key={`option-action-${index}`}
                  action={action}
                  fieldIndex={fieldIndex}
                  itemIndex={index}
                />
              );
            })
          )}
        </div>
      </HubertPopover.Content>
    </HubertPopover>
  );
};

export default OptionActionPopover;
